<script setup lang="ts">
import { Mask } from 'maska';
import type Address from './Address.vue';
import type AntiRobot from '@/components/base/form/AntiRobot.vue';
import type StrengthBar from '@/components/auth/StrengthBar.vue';
import IconWrapper from '@/components/base/IconWrapper.vue';
import type { UserDocument } from '@/types/UserData';

const { $metrics } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { referenceCode } = useParameters();
const { ApiService } = useApiService();
const { getFormattedBrazilianDate } = useDateTimeHelper();

const {
  isCaptchaRegisterEnabled,
  isCaptchaValidateCpfEnabled,
  isInvisibleCaptcha,
} = useCaptcha();

const { isAuthV2FlowEnabled, isRegulatorySimplifiedRegisterEnabled } =
  useAuth();

const registerData = ref<any>({
  cpf: '',
  birth_date: '',
  name: '',
  email: '',
  ddi: '',
  phone: '',
  password: '',
  nationalities: [],
});

const nationalities = [
  { id: 1, value: 'Brasileiro (a)' },
  { id: 4, value: 'Argentino (a)' },
  { id: 13, value: 'Português (a)' },
  { id: 12, value: 'Mexicano (a)' },
  { id: 2, value: 'Alemão (a)' },
  { id: 3, value: 'Americano (a)' },
  { id: 5, value: 'Canadense (a)' },
  { id: 6, value: 'Chileno (a)' },
  { id: 7, value: 'Espanhol (a)' },
  { id: 8, value: 'Francês (a)' },
  { id: 9, value: 'Indiano (a)' },
  { id: 10, value: 'Italiano (a)' },
  { id: 11, value: 'Japonês (a)' },
  { id: 14, value: 'Russo (a)' },
  { id: 15, value: 'Sueco (a)' },
  { id: 16, value: 'Suíço (a)' },
  { id: 17, value: 'Outro' },
];

const isLoadingRegister = ref(false);
const documentField = ref<string>('');
const registerDataSingleOptIn = ref<boolean>(false);

const documentNameOffuscated = computed<string>(() => {
  if (!registerData.value.name) return '';

  const names = registerData.value.name.split(' ');
  const firstName = names[0];
  const remainingNames = names
    .slice(1)
    .map((name: string) => name.replace(/\B\w/g, '*'))
    .join(' ');

  return `${firstName} ${remainingNames}`;
});

const unmaskedCpf = computed<string>(() => {
  const mask = new Mask({ mask: '###.###.###-##' });

  return mask.unmasked(documentField.value);
});

function onDocumentRemoved() {
  registerData.value.name = '';
  registerData.value.document = '';

  resetAntiRobotToken();

  addressRef.value?.removeAddress();
}

function onDocumentValidated(data: UserDocument) {
  if (data.name) {
    registerData.value.name = data.name;
  }
  if (data.birth_date) {
    registerData.value.birth_date = data.birth_date;
  }

  registerData.value.document = unmaskedCpf.value;

  resetAntiRobotToken();
}

const isCpfValid = computed<boolean>(() => {
  if (!registerData.value.document) {
    return false;
  }

  return true;
});

const ddiError = ref<string>('');

const selectedDdi = ref({
  id: 4,
  name: 'Brasil',
  code: 'BRA',
  ddi: '+55',
  currency: 'BRL',
  alpha2: 'BR',
});

const maskByCountryPhone = computed<string[] | string>(() => {
  if (!selectedDdi.value.code) return '';

  // @ts-ignore
  return phoneMaksInAlpha3[selectedDdi.value.code] ?? '';
});

function removeMask(str: string, number?: boolean) {
  if (number) {
    return str.replace(/[^\d]/gi, '');
  }

  return str.replace(/[^\w]/gi, '');
}

const { allCountriesList } = storeToRefs(useCountriesStore());

const countriesSortDdi = computed(() => {
  if (!allCountriesList?.value) {
    return;
  }

  return allCountriesList.value.sort((a: any, b: any) => a.ddi - b.ddi);
});

function countMaskLength(mask: any) {
  if (!Array.isArray(mask) || mask.length === 0) {
    return 0;
  }

  return mask.reduce((min, item) => Math.min(min, item.length), Infinity);
}

const strengthBarRef = ref<InstanceType<typeof StrengthBar> | null>(null);
const isStrengthBarVisible = ref(false);

function updatePasswordStrength() {
  strengthBarRef.value?.updatePasswordStrength();
}

const passwordIsValid = computed(() => {
  return strengthBarRef.value?.passwordIsValid;
});

const addressRef = ref<InstanceType<typeof Address> | null>(null);

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed<string>(() => {
  if (!isCaptchaRegisterEnabled.value && !isCaptchaValidateCpfEnabled.value) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (
    isCaptchaValidateCpfEnabled.value &&
    !isCaptchaRegisterEnabled.value &&
    isCpfValid.value
  ) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (isInvisibleCaptcha.value) {
    return '-mt-1 mb-0 relative';
  }

  return 'm-0';
});

function resetAntiRobotToken() {
  antiRobotRef.value?.$resetAllCaptcha();
}

function handleErrorRegister(err: any, fallbackMessageKey: string): void {
  console.error(err);

  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  openToaster({
    message: errorMessage,
    type: 'error',
  });

  resetAntiRobotToken();
}

async function handleSuccessRegister(
  res: any,
  successCallback: (res: any) => void,
): Promise<void> {
  if (res.status.toString().startsWith('20')) {
    openToaster({
      message: t('auth.register.success'),
      type: 'success',
    });

    if (res.data) {
      $metrics.push('gtm')?.handle({
        event: 'sign_up',
        ...gtmUserParams(res.data?.user),
      });

      $metrics.push('facebook')?.handle('track', 'Lead', {
        content_name: 'Cadastro',
        status: true,
        value: 1,
      });

      const { isTaboolaEnabled, isKwaiPixelEnabled } = useFeatureFlagsEnv();

      if (isKwaiPixelEnabled.value) {
        $metrics.push('kwai')?.handle('addToCart');
      }

      if (isTaboolaEnabled.value) {
        $metrics.push('taboola')?.handle('lead');
      }

      if (window.self !== window.top) {
        window.parent.postMessage({
          message: 'loginSuccess',
          responseData: res.data,
          responseHeaders: {
            'X-INFOS-ID':
              res.headers.get('X-INFOS-ID') ||
              res.headers.get('X-Infos-Id') ||
              res.headers.get('x-infos-id'),
            'X-INFOS-REF':
              res.headers.get('X-INFOS-REF') ||
              res.headers.get('X-Infos-Ref') ||
              res.headers.get('x-infos-ref'),
          },
        });
      }

      const { setAuth } = useAuthStore();

      await setAuth(res);

      const { closeAuth } = useAuth();
      const { startSmartico } = useSmarticoInit();

      closeAuth();
      startSmartico();
    }

    successCallback(res);
  } else {
    openToaster({
      message: t('auth.register.error.status_error'),
      type: 'error',
    });
  }
}

const { fieldError, fieldErrorEffect, setFieldError } = useFieldError();

async function handleRegisterSubmit(typeSubmit?: boolean) {
  fieldError.value = '';
  fieldErrorEffect.value = '';

  if (!registerData.value.document) {
    setFieldError(
      'document',
      t('auth.register.singlestep.errors.type_document'),
    );

    return;
  }

  if (!isEmailValid(registerData.value.email)) {
    setFieldError('email', t('auth.register.singlestep.errors.type_email'));

    return;
  }

  if (registerData.value.ddi.length < 2) {
    setFieldError('phone', t('auth.register.singlestep.errors.select_ddi'));

    return;
  }

  if (
    registerData.value.phone.length < countMaskLength(maskByCountryPhone.value)
  ) {
    setFieldError('phone', t('auth.register.singlestep.errors.type_phone'));

    return;
  }
  if (!registerData.value.password) {
    setFieldError(
      'password',
      t('auth.register.singlestep.errors.type_password'),
    );

    return;
  }

  if (!passwordIsValid.value) {
    setFieldError(
      'password',
      t('auth.register.singlestep.errors.weak_password'),
    );

    return;
  }

  if (registerData.value.nationalities.length === 0) {
    setFieldError(
      'nationalities',
      t('auth.register.singlestep.errors.select_nationality'),
    );

    return;
  }

  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    setFieldError(
      'captcha',
      t('auth.register.singlestep.errors.complete_captcha'),
    );

    return;
  }
  if (!registerDataSingleOptIn.value) {
    setFieldError('terms', t('auth.register.singlestep.errors.accept_terms'));

    return;
  }

  const payload: IRegisterSingleStepSubmitData = {
    country: 'BRA',
    email: registerData.value.email,
    nationalities: JSON.stringify(registerData.value.nationalities),
    optIn: true,
    captcha_token: antiRobotToken.value,
  };

  payload.number = registerData.value.document;

  payload.ddi = registerData.value.ddi;
  payload.phone = removeMask(registerData.value.phone);

  payload.password = registerData.value.password;

  if (referenceCode.value) {
    payload.affiliation_code = String(referenceCode.value);
  }
  if (isRegulatorySimplifiedRegisterEnabled.value) {
    payload.user_prudential_limit_active = typeSubmit;
  }

  const { utmSrc, utmSource, utmCampaign, utmContent, utmMedium, gaClientId } =
    useParameters();

  if (utmSrc.value) {
    payload.src = utmSrc.value;
  }

  if (utmSource.value) {
    payload.utm_source = utmSource.value;
  }

  if (utmCampaign.value) {
    payload.utm_campaign = utmCampaign.value;
  }

  if (utmContent.value) {
    payload.utm_content = utmContent.value;
  }

  if (utmMedium.value) {
    payload.utm_medium = utmMedium.value;
  }

  if (gaClientId.value) {
    payload.ga_client_id = gaClientId.value;
  }

  console.debug(payload);

  await initEmailRegister(payload);
}

async function initEmailRegister(payload: any) {
  isLoadingRegister.value = true;

  try {
    const res = await ApiService.post('/bff/register-simplified', payload);

    await handleSuccessRegister(res, () => {
      if (res.data) {
        return res;
      }
    });
  } catch (err) {
    handleErrorRegister(err, 'auth.register.error.api_error');
    isLoadingRegister.value = false;
  }
}

onMounted(() => {
  registerData.value.nationalities = [nationalities[0]];

  setTimeout(() => {
    registerData.value.phone = '';
  }, 100);
});

watch(
  () => selectedDdi.value.ddi,
  () => {
    registerData.value.ddi = selectedDdi.value.ddi;
    registerData.value.phone = '';
  },
  { immediate: true },
);

watch(
  () => registerData.value.phone,
  (newValue) => {
    const onlyNumbers = removeMask(newValue, true);

    if (
      onlyNumbers.length === 11 &&
      newValue.includes('.') &&
      newValue.includes('-')
    ) {
      registerData.value.phone = '';
    }
  },
);
</script>

<template>
  <section :class="$style.registerContainer">
    <BaseLoading
      class="!bg-auth-bg-primary z-50"
      :show="isLoadingRegister"
    />

    <div :class="$style.registerWrapper">
      <div :class="$style.registerFields">
        <div
          :class="[
            $style.documentContainer,
            { [$style.fieldHasError]: fieldErrorEffect === 'document' },
          ]"
        >
          <div
            v-if="!registerData.document"
            :class="$style.fieldButton"
          >
            <BaseFormInputCpf
              id="cpf"
              v-model:masked="documentField"
              :anti-robot-token="antiRobotToken ?? ''"
              :has-error="fieldError === 'document'"
              hide-icon
              :label="$t('auth.register.cpf')"
              name="cpf"
              placeholder="ex: 123.456.789-00"
              required
              @focus="fieldError = ''"
              @remove-document="onDocumentRemoved"
              @reset-anti-robot-token="resetAntiRobotToken"
              @validate-document="onDocumentValidated"
            />
          </div>

          <div
            v-else
            :class="$style.documentDetails"
          >
            <div :class="$style.documentValues">
              <div
                v-if="registerData.name"
                :class="$style.documentName"
              >
                {{ documentNameOffuscated }}
              </div>
              <div
                v-if="registerData.document"
                :class="$style.documentVal"
              >
                <b>{{ documentField }}</b>
                <span :class="$style.pipe">|</span>
                <span v-if="registerData.birth_date">
                  {{ getFormattedBrazilianDate(registerData.birth_date) }}
                </span>
                <span
                  v-else
                  :class="$style.hasError"
                >
                  <NuxtIcon name="close" />
                  {{ $t('auth.register.singlestep.errors.birth_date') }}
                </span>
              </div>
            </div>

            <button
              :class="$style.changeButton"
              @click="onDocumentRemoved"
            >
              {{ $t('auth.register.remove_document') }}
            </button>
          </div>
        </div>

        <div
          :class="[
            $style.emailField,
            { [$style.fieldHasError]: fieldErrorEffect === 'email' },
          ]"
        >
          <BaseFormInput
            id="email"
            v-model="registerData.email"
            autocomplete="off"
            :info="$t('auth.register.singlestep.email_info')"
            :label="$t('auth.register.email')"
            name="email"
            placeholder="ex: alguem@email.com"
            :required="true"
            type="email"
            @focus="fieldError = ''"
          />
        </div>

        <div
          :class="[
            $style.phoneField,
            { [$style.fieldHasError]: fieldErrorEffect === 'phone' },
          ]"
        >
          <BaseFormInput
            id="phone"
            :key="`phone-input-${selectedDdi.ddi}`"
            v-model="registerData.phone"
            autocapitalize="none"
            autocomplete="nope"
            autocorrect="off"
            data-form-type="other"
            data-lpignore="true"
            :data-maska="maskByCountryPhone"
            :data-maska-eager="false"
            :has-error="fieldError === 'phone'"
            :info="$t('auth.register.singlestep.phone_info')"
            inputmode="tel"
            :label="$t('auth.register.phone')"
            name="phone"
            pattern="[0-9]*"
            placeholder="ex: (31) 99876-5432"
            readonly
            :required="true"
            spellcheck="false"
            type="tel"
            @click="
              ($event.target as HTMLInputElement).removeAttribute('readonly')
            "
            @focus="
              fieldError = '';
              ($event.target as HTMLInputElement).removeAttribute('readonly');
            "
          >
            <template #prepend>
              <HeadlessListbox
                v-model="selectedDdi"
                as="div"
                :class="$style.ddiSelect"
              >
                <HeadlessListboxButton
                  :class="$style.ddiBtn"
                  @click="ddiError = ''"
                >
                  <IconWrapper
                    v-if="selectedDdi.code"
                    class="text-xl mr-2"
                    :name="`flags/${selectedDdi.code}`"
                  />
                  {{ selectedDdi.ddi || 'DDI' }}
                  <NuxtIcon
                    v-if="!selectedDdi.code"
                    class="ml-3 opacity-30"
                    name="arrows/angle_down"
                  />
                </HeadlessListboxButton>

                <HeadlessListboxOptions :class="$style.ddiOptionsGroup">
                  <div class="px-1 py-1 relative overflow-auto h-full">
                    <HeadlessListboxOption
                      v-for="option in countriesSortDdi"
                      :key="option.id"
                      :value="option"
                    >
                      <span :class="[$style.ddiOption, 'group']">
                        <IconWrapper
                          class="text-xl mr-2"
                          :name="`flags/${option.code}`"
                        />
                        {{ option.ddi }}
                      </span>
                    </HeadlessListboxOption>
                  </div>
                </HeadlessListboxOptions>
              </HeadlessListbox>
            </template>
          </BaseFormInput>
        </div>

        <div :class="$style.rowColumns">
          <div
            :class="[
              $style.passwordContainer,
              { [$style.fieldHasError]: fieldErrorEffect === 'password' },
            ]"
          >
            <BaseFormInput
              id="password"
              v-model="registerData.password"
              autocomplete="password"
              :has-error="fieldError === 'password'"
              :label="$t('auth.register.password')"
              name="password"
              placeholder="crie uma senha segura..."
              :required="true"
              :show-hide-pass="true"
              @blur="isStrengthBarVisible = false"
              @focus="
                isStrengthBarVisible = true;
                fieldError = '';
              "
              @keyup="updatePasswordStrength"
            />

            <div
              v-show="isStrengthBarVisible"
              :class="$style.strengthBarContainer"
            >
              <AuthStrengthBar
                ref="strengthBarRef"
                :compact-mode="true"
                :password="registerData.password"
              />

              <button
                :class="$style.closeButton"
                @click="isStrengthBarVisible = false"
              >
                {{ $t('auth.register.singlestep.complete') }}
              </button>
            </div>
          </div>
        </div>

        <section
          :class="[
            $style.checkboxSection,
            { [$style.hasError]: fieldError === 'terms' },
            { [$style.fieldHasError]: fieldErrorEffect === 'terms' },
          ]"
        >
          <BaseFormCheckbox
            v-model="registerDataSingleOptIn"
            :label="$t('auth.register.singlestep.optin')"
            value="termsAgreement"
          />
        </section>

        <div
          v-if="referenceCode"
          :class="$style.referenceCodeContainer"
        >
          {{ $t('auth.register.reference_code') }}:
          <strong>
            {{ referenceCode }}
          </strong>
        </div>

        <div :class="$style.buttonsContainer">
          <button @click.prevent="handleRegisterSubmit(false)">
            {{ $t('auth.register.complete_regulatory.button_finish_register') }}
          </button>
        </div>

        <div
          v-if="isRegulatorySimplifiedRegisterEnabled"
          :class="$style.buttonAccountSecure"
        >
          <button @click.prevent="handleRegisterSubmit(true)">
            {{
              $t(
                'auth.register.complete_regulatory.button_create_account_secure',
              )
            }}
          </button>
        </div>

        <div :class="captchaComponentClass">
          <BaseFormAntiRobot
            ref="antiRobotRef"
            :field-has-error="fieldErrorEffect === 'captcha'"
          />
        </div>

        <BaseModalFooter v-if="!isAuthV2FlowEnabled" />
      </div>
    </div>
  </section>
</template>

<style module lang="postcss">
.registerContainer {
  @apply w-full flex flex-col min-h-full;

  .registerWrapper {
    @apply w-full flex-grow overflow-auto flex flex-col pb-2 pt-4 relative;

    .orDivider {
      @apply flex items-center justify-center gap-4 text-xs font-semibold text-auth-texts/40 my-3 md:my-4 uppercase leading-3;

      &:before,
      &:after {
        content: '';
        @apply h-[1px] bg-auth-texts/10 flex-1 mt-0.5;
      }
    }

    .separator {
      @apply h-0 mb-3;
    }

    .registerFields {
      @apply flex flex-col gap-2.5 md:gap-3;

      .fieldHasError {
        @apply border-error;
        animation: shake 200ms ease-in-out;
      }

      .documentContainer {
        @apply flex flex-col;

        .fieldButton {
          @apply w-full h-[46px] sm:h-[50px] bg-[#0C1B44] rounded-[10px] relative;
        }

        .documentDetails {
          @apply flex justify-between items-center gap-0.5 sm:gap-2 relative bg-[#0C1B44] rounded-[10px] w-full h-[46px] sm:h-[50px];

          .documentValues {
            @apply flex flex-col gap-0 sm:gap-px px-3 text-2xs;

            .documentName {
              @apply text-auth-texts/60;
            }

            .documentVal {
              @apply text-auth-texts font-semibold flex items-center;

              b {
                @apply text-primary font-semibold;
              }

              .pipe {
                @apply text-auth-texts/30 mx-2;
              }

              .hasError {
                @apply text-error text-2xs inline-flex items-center gap-1;
              }
            }
          }

          .changeButton {
            @apply absolute right-4 text-[.6rem] text-auth-text-inputs flex justify-center items-center rounded px-2 h-5 text-nowrap flex-nowrap bg-auth-text-inputs/20 whitespace-nowrap;
          }
        }
      }

      .emailField {
        @apply relative bg-[#0C1B44] rounded-[10px] h-[46px] sm:h-[50px];

        .socialAuthInfo {
          @apply absolute right-0 top-0 text-lg flex justify-center items-center w-[46px] sm:w-[50px] h-[46px] sm:h-[50px];
        }
      }

      .phoneField {
        @apply bg-[#0C1B44] rounded-[10px] h-[46px] sm:h-[50px];
      }

      .ddiSelect {
        @apply relative inline-block text-left text-texts border-solid border-r border-auth-text-inputs/20;
        .ddiBtn {
          @apply inline-flex w-full justify-center items-center rounded-none px-4 py-2 text-sm font-medium;
        }
        .ddiOptionsGroup {
          @apply absolute left-0 mt-2 w-24 origin-top-right divide-y divide-white rounded-md bg-[#0C1B44] shadow-lg ring-1 ring-black/5 focus:outline-none z-[999] overflow-hidden h-[200px];
        }
        .ddiOption {
          @apply flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer;
        }
      }

      .rowColumns {
        @apply flex gap-2 md:gap-3 items-center justify-center max-w-full relative;

        .passwordContainer {
          @apply relative z-10 flex-1 bg-[#0C1B44] rounded-[10px] h-[46px] sm:h-[50px];

          .strengthBarContainer {
            @apply absolute top-full left-0 w-full bg-[#0C1B44] p-3 -mt-2 rounded-b-[10px] shadow-lg shadow-black/50;

            .closeButton {
              @apply text-xs text-auth-text-inputs border border-auth-text-inputs/50 rounded-md px-2 py-1 mt-3;
            }
          }
        }

        .nationalitiesContainer {
          @apply flex gap-2 md:gap-3 items-center justify-center max-w-full relative min-w-40;

          &.isSocialFlow {
            @apply min-w-full;
          }
        }
      }

      .addressContainer {
        @apply relative w-full;
      }

      .checkboxSection {
        @apply px-3 py-2 rounded-[10px] flex items-start;
        background-color: #0c1b44 !important;

        &.hasError {
          box-shadow: 0 0 200px inset rgba(255, 0, 0, 0.1);
        }

        p {
          @apply text-2xs leading-4;
        }

        a {
          @apply text-primary underline cursor-pointer whitespace-nowrap;
        }

        b {
          @apply font-semibold text-primary;
        }
      }

      .referenceCodeContainer {
        @apply text-auth-texts text-2xs leading-3 font-medium flex justify-center gap-1.5 relative bg-[#0C1B44] rounded-[10px] whitespace-nowrap px-3 py-2;

        strong {
          @apply text-primary font-semibold overflow-hidden overflow-ellipsis;
        }
      }

      .buttonsContainer {
        @apply w-full flex justify-center mx-auto whitespace-nowrap pt-3;

        button {
          @apply w-full h-12 px-4 py-3 rounded-[10px] inline-flex justify-center items-center gap-2.5 transition text-white font-semibold;
          background: radial-gradient(
            ellipse 138% 168.3% at 36.76% -16.67%,
            #ff704a 0%,
            #ff3400 100%
          );

          &:hover {
            filter: brightness(1.1);
          }

          &:disabled {
            @apply opacity-40 cursor-not-allowed;
          }
        }
      }
      .buttonAccountSecure {
        @apply w-full flex justify-center mx-auto whitespace-nowrap;

        button {
          @apply w-full bg-none text-texts rounded-md py-3 font-semibold flex items-center justify-center gap-2 text-sm underline;

          &:disabled {
            @apply opacity-40 cursor-not-allowed;
          }
        }
      }

      /* força transparência no root dos componentes base quando passamos a classe no template */
      .clearBg {
        background-color: transparent !important;
        box-shadow: none !important;
        border: none !important;
      }

      /* Estilo fixo dos campos para aparecer o fundo azul escuro */
      .emailField :global(.input-group),
      .phoneField :global(.input-group),
      .passwordContainer :global(.input-group),
      .documentContainer :global(.input-group) {
        background-color: transparent !important;
        box-shadow: none !important;
        height: 100%;
        width: 100%;
        border-radius: inherit;
      }

      .emailField :global(.group),
      .phoneField :global(.group),
      .passwordContainer :global(.group) {
        @apply h-[46px] sm:h-[50px] bg-transparent;
        padding-left: 1rem;
        padding-right: 1rem;
      }

      .documentContainer :global(.group) {
        @apply h-[46px] sm:h-[50px] bg-transparent;
        padding-left: 1rem !important;
        padding-right: 1rem !important;
      }

      .emailField :global(.input),
      .phoneField :global(.input),
      .passwordContainer :global(.input),
      .documentContainer :global(.input) {
        @apply text-white placeholder-white/70;
        background-color: transparent !important;
      }

      .emailField :global(.label),
      .phoneField :global(.label),
      .passwordContainer :global(.label),
      .documentContainer :global(.label) {
        @apply text-white/70;
      }

      .documentContainer :global(.input-group) {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  }
}

@keyframes shake {
  0% {
    transform: translateX(-3px);
  }
  25% {
    transform: translateX(3px);
  }
  50% {
    transform: translateX(-3px);
  }
  75% {
    transform: translateX(3px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
